// ============================================
// FILE: /frontend/src/pages/SubscriptionHistoryPage.jsx
// PURPOSE: Subscription payment history and invoices
// ============================================

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Calendar, Download, CreditCard, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../api/client';

export default function SubscriptionHistoryPage() {
  console.log('[SubscriptionHistoryPage] Component mounted');

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    console.log('[SubscriptionHistoryPage] Fetching subscription history...');
    setLoading(true);

    try {
      const response = await apiClient.get('/subscription/history');
      console.log('[SubscriptionHistoryPage] History received:', response.data);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('[SubscriptionHistoryPage] Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to load subscription history');
    } finally {
      setLoading(false);
    }
  };

    const downloadInvoice = async (transaction) => {
    console.log('[SubscriptionHistoryPage] Downloading invoice:', transaction.id);
    
    try {
      const response = await apiClient.get(`/subscription/invoice/${transaction.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${transaction.id}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('[SubscriptionHistoryPage] Invoice downloaded successfully');
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('[SubscriptionHistoryPage] Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      COMPLETED: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.PENDING;
  };

  const getPlanIcon = (plan) => {
    const icons = {
      BRONZE: '🥉',
      SILVER: '🥈',
      GOLD: '🥇',
      PLATINUM: '💎'
    };
    return icons[plan] || '📦';
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Subscription History</h1>
          <button
            onClick={() => {
              console.log('[SubscriptionHistoryPage] Refresh clicked');
              fetchHistory();
            }}
            disabled={loading}
            className="px-4 py-2 border rounded-md hover:bg-muted transition text-sm disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No payment history found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Your subscription payments will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4 flex-1">
                        <div className="text-3xl">
                          {getPlanIcon(transaction.plan)}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{transaction.plan} Plan</h3>
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBadge(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(transaction.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-4 h-4" />
                              {transaction.paymentMethod}
                            </span>
                          </div>

                          <p className="text-xs text-muted-foreground">
                            Transaction ID: {transaction.transactionId}
                          </p>
                        </div>
                      </div>

                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="text-xl font-bold">
                          KES {transaction.amount.toLocaleString()}
                        </p>
                        
                        {transaction.status === 'COMPLETED' && transaction.amount > 0 && (
                          <button
                            onClick={() => downloadInvoice(transaction)}
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <Download className="w-4 h-4" />
                            Invoice
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Card */}
        {history.length > 0 && (
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="py-6">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                  <p className="text-2xl font-bold text-primary">
                    KES {history.filter(t => t.status === 'COMPLETED').reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Transactions</p>
                  <p className="text-2xl font-bold text-primary">{history.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Success Rate</p>
                  <p className="text-2xl font-bold text-primary">
                    {Math.round((history.filter(t => t.status === 'COMPLETED').length / history.length) * 100)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}